/**
 * The ten visual modes.
 *
 * This file is metadata only — labels, and the three chips the switcher paints.
 * The actual palettes live in `src/theme.css`, keyed by the same ids. If you add
 * a theme here you must add a matching `[data-theme="<id>"]` block there, and
 * `src/theme.test.js` will fail until you do.
 *
 * `group` splits the switcher menu. The first six vary colour and texture over
 * one shared arrangement; the last four also change layout.
 */

export const THEMES = [
  {
    id: 'paper',
    label: 'Paper',
    hint: 'Ink on warm paper',
    scheme: 'light',
    group: 'colour',
    swatch: ['#fbfaf7', '#0e6e4e', '#12140f'],
  },
  {
    id: 'ink',
    label: 'Ink',
    hint: 'The same page, at night',
    scheme: 'dark',
    group: 'colour',
    swatch: ['#101210', '#5fd39b', '#f2f1ea'],
  },
  {
    id: 'midnight',
    label: 'Midnight',
    hint: 'Neutral greys, softer edges',
    scheme: 'dark',
    group: 'colour',
    swatch: ['#0e0f11', '#8ab4ff', '#e8eaed'],
  },
  {
    id: 'blueprint',
    label: 'Blueprint',
    hint: 'Drafting cyan on navy',
    scheme: 'dark',
    group: 'colour',
    swatch: ['#0b1a2e', '#7cc4ff', '#e4eefc'],
  },
  {
    id: 'terminal',
    label: 'Terminal',
    hint: 'Phosphor on black',
    scheme: 'dark',
    group: 'colour',
    swatch: ['#080a08', '#4ade80', '#c8f5d2'],
  },
  {
    id: 'solar',
    label: 'Solar',
    hint: 'Hard shadows, warm light',
    scheme: 'light',
    group: 'colour',
    swatch: ['#fff6e6', '#a8500a', '#1c1408'],
  },
  {
    id: 'index',
    label: 'Index',
    hint: 'A dense directory, no images',
    scheme: 'light',
    group: 'layout',
    swatch: ['#ffffff', '#c8102e', '#0a0a0a'],
  },
  {
    id: 'gallery',
    label: 'Gallery',
    hint: 'Images first, text as caption',
    scheme: 'light',
    group: 'layout',
    swatch: ['#efeeec', '#2f5d50', '#1a1a18'],
  },
  {
    id: 'zine',
    label: 'Zine',
    hint: 'One narrow column, big type',
    scheme: 'light',
    group: 'layout',
    swatch: ['#faf6f0', '#7a2f52', '#1a1512'],
  },
  {
    id: 'console',
    label: 'Console',
    hint: 'Four columns, mono, dense',
    scheme: 'dark',
    group: 'layout',
    swatch: ['#12161a', '#ffb340', '#dfe5ec'],
  },
];

export const THEME_IDS = THEMES.map((theme) => theme.id);

export const DEFAULT_THEME = 'blueprint';

export function isThemeId(value) {
  return typeof value === 'string' && THEME_IDS.includes(value);
}

export function themeById(id) {
  // Fall back to DEFAULT_THEME, not array position — the two aren't the same
  // theme once the default isn't the first entry in THEMES.
  return THEMES.find((theme) => theme.id === id)
    || THEMES.find((theme) => theme.id === DEFAULT_THEME);
}

// Used by Shift+T. An unknown id lands on the default rather than throwing,
// so a stale ?theme= value in the URL cannot wedge the shortcut. Handled
// explicitly rather than via the indexOf(-1) + 1 = 0 trick, which only landed
// on the default back when it also happened to be THEME_IDS[0].
export function nextThemeId(id) {
  const index = THEME_IDS.indexOf(id);
  if (index === -1) return DEFAULT_THEME;
  return THEME_IDS[(index + 1) % THEME_IDS.length];
}
